import { createFileRoute } from "@tanstack/react-router";
import React, { useState } from "react";
import {
  AlertCircle,
  Award,
  Briefcase,
  Check,
  CheckCircle2,
  Clock,
  Download,
  FileText,
  GraduationCap,
  Mail,
  Pencil,
  Phone,
  Plus,
  ShieldCheck,
  User,
  Wallet,
  X,
} from "lucide-react";
import { useAuthStore } from "@/store/authStore";
import { useStudentProfile, useUpdateStudentProfile } from "@/hooks/useStudentData";
import PageHeader from "@/components/shared/PageHeader";
import apiClient from "@/lib/api-client";
import { taskpartStudentTiers } from "@/data/taskpartProduct";
import { Link } from "@tanstack/react-router";
import { TierBadge } from "@/components/shared/TierBadge";
import { toast } from "sonner";
import AvatarUploadModal from "@/components/shared/AvatarUploadModal";
import { getFileUrl } from "@/lib/api-client";
import PaymentModal from "@/components/shared/PaymentModal";
import { startSnippeCheckout, usePaymentReturn } from "@/lib/payments";
import { usePricing } from "@/hooks/usePricing";
import { IconAction } from "@/components/shared/IconAction";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field, PasswordInput } from "@/components/forms";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/student/profile/")({ component: StudentProfile });

type ProfileFormState = {
  first_name: string;
  last_name: string;
  student_id: string;
  phone: string;
  university: string;
  bio: string;
  gender: string;
  skills: string[];
};

function StudentProfile() {
  const { user } = useAuthStore();
  const { data: profile } = useStudentProfile();
  const updateProfile = useUpdateStudentProfile();
  const { data: pricing } = usePricing();
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [selectedTier, setSelectedTier] = useState("");
  const [tab, setTab] = useState(0);
  const [, setSaved] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState<string | null>(null);
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);
  const [twoFactorDialogOpen, setTwoFactorDialogOpen] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const { setUser } = useAuthStore();

  const [formData, setFormData] = useState<ProfileFormState>({
    first_name: "",
    last_name: "",
    student_id: "",
    phone: "",
    university: "",
    bio: "",
    gender: "Male",
    skills: [],
  });

  React.useEffect(() => {
    if (profile) {
      setFormData({
        first_name: profile.first_name || "",
        last_name: profile.last_name || "",
        student_id: profile.student_id || "",
        phone: profile.phone || "",
        university: profile.university || "",
        bio: profile.bio || "",
        gender: profile.gender || "Male",
        skills: profile.skills ? profile.skills.split(",").map((s: string) => s.trim()) : [],
      });
    }
  }, [profile]);

  const handleSave = async () => {
    try {
      await updateProfile.mutateAsync({
        ...formData,
        skills: Array.isArray(formData.skills) ? formData.skills.join(", ") : formData.skills,
      });
      setSaved(true);
      toast.success("Profile Updated", { description: "Your changes are now live." });
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      toast.error("Update failed");
    }
  };

  const userTier = user?.membershipTier || "free";
  const verificationStatus = user?.verificationStatus || "pending";
  const tiers = pricing?.tiers || taskpartStudentTiers;
  const activeTier = tiers.find((t) => t.id === userTier) || tiers[0];

  const handleDocumentUpload = async (type: string, file?: File | null) => {
    if (!file) return;
    try {
      setUploadingDoc(type);
      const form = new FormData();
      form.append("file", file);
      form.append("type", type);
      await apiClient.post("/students/me/documents", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document uploaded successfully");
      window.location.reload();
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploadingDoc(null);
    }
  };

  const handleUpgrade = (tierId: string) => {
    setSelectedTier(tierId);
    setUpgradeOpen(true);
  };

  const getAmountForTier = (tierId: string) => {
    if (tierId === "verified") return parseInt(pricing?.verifiedPrice || "10000");
    if (tierId === "premium") return parseInt(pricing?.premiumPrice || "50000");
    return 0;
  };

  // Paying for a tier goes through Snippe checkout; the API upgrades the
  // membership once Snippe confirms the payment.
  const handlePaymentSuccess = () =>
    startSnippeCheckout({
      type: selectedTier === "premium" ? "premium" : "verification",
      returnPath: "/student/profile",
    });

  usePaymentReturn((status) => {
    if (status === "completed" || status === "verified") {
      window.location.reload();
    }
  });

  const handleEnable2FA = async () => {
    try {
      await apiClient.post("/auth/security/2fa/setup");
      setTwoFactorDialogOpen(true);
    } catch (err) {
      toast.error("Failed to start 2FA setup");
    }
  };

  const verify2FASetup = async () => {
    try {
      await apiClient.post("/auth/security/2fa/verify-setup", { code: twoFactorCode });
      setTwoFactorDialogOpen(false);
      if (user) setUser({ ...user, two_factor_enabled: true } as any);
      toast.success("2FA enabled successfully");
    } catch (err) {
      toast.error("Invalid verification code");
    }
  };

  const handleDisable2FA = async () => {
    try {
      await apiClient.delete("/auth/security/2fa/disable");
      if (user) setUser({ ...user, two_factor_enabled: false } as any);
      toast.success("2FA disabled");
    } catch (err) {
      toast.error("Failed to disable 2FA");
    }
  };

  const nextTier = userTier === "free" ? "verified" : userTier === "verified" ? "premium" : null;

  return (
    <div className="space-y-6 pb-8">
      <PageHeader
        title="Account workspace"
        subtitle="Refine your professional identity and manage institutional verifications."
        breadcrumbs={[{ label: "Dashboard", path: "/student/dashboard" }, { label: "Profile" }]}
        action={
          <>
            <Button variant="outline">Preview resume</Button>
            <Button onClick={handleSave} disabled={updateProfile.isPending}>
              {updateProfile.isPending ? "Saving..." : "Save changes"}
            </Button>
          </>
        }
      />

      {user?.verificationStatus === "pending_review" && (
        <Alert variant="info">
          <Clock />
          <div>
            <AlertTitle>Institutional verification pending</AlertTitle>
            <AlertDescription className="text-body">
              An administrator is currently reviewing your documents. You will be able to upgrade
              your tier once your status is "Verified".
            </AlertDescription>
          </div>
        </Alert>
      )}

      {user?.verificationStatus === "rejected" && (
        <Alert variant="destructive">
          <AlertCircle />
          <div>
            <AlertTitle>Verification rejected</AlertTitle>
            <AlertDescription className="text-body">
              Your verification was rejected. Please review your uploaded documents and ensure
              they are clear and valid.
            </AlertDescription>
          </div>
        </Alert>
      )}

      <div className="grid items-start gap-6 lg:grid-cols-[320px_minmax(0,1fr)]">
        {/* Left column */}
        <div className="flex min-w-0 flex-col gap-4">
          <section className="rounded-md border border-border bg-card p-5 text-center md:p-6">
            <div className="relative mx-auto mb-4 size-24">
              <Avatar className="size-24 rounded-full">
                <AvatarImage src={getFileUrl(user?.avatar)} alt="" />
                <AvatarFallback className="text-[28px] font-normal">
                  {user?.name?.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <IconAction
                label="Change photo"
                icon={<Pencil />}
                variant="outline"
                onClick={() => setAvatarModalOpen(true)}
                className="absolute -right-2 -bottom-2 bg-card"
              />
            </div>

            <div className="mb-1 flex flex-wrap items-center justify-center gap-2">
              <h2 className="text-title text-foreground">{user?.name}</h2>
              <TierBadge
                tier={userTier as any}
                verificationStatus={verificationStatus}
                minimal={false}
                size="small"
              />
            </div>
            <p className="mb-5 font-mono text-caption text-muted-foreground">
              ID: {profile?.student_id || "Unassigned"}
            </p>

            <Separator className="mb-5" />

            <ul className="space-y-3 text-left">
              <InfoItem icon={<Mail />} label="Email" value={user?.email} />
              <InfoItem icon={<Phone />} label="Phone" value={profile?.phone || "Add phone"} />
              <InfoItem
                icon={<GraduationCap />}
                label="University"
                value={profile?.university || "Not set"}
              />
            </ul>
          </section>

          {/* Membership */}
          <section className="rounded-md border border-border bg-card p-5">
            <p className="eyebrow mb-2">Current status</p>
            <h2 className="mb-1 text-title text-foreground">{activeTier.name}</h2>
            <p className="mb-5 text-sm leading-6 text-muted-foreground">
              {activeTier.description}
            </p>

            {nextTier ? (
              <Button variant="outline" className="w-full" onClick={() => handleUpgrade(nextTier)}>
                <Award />
                Upgrade to {nextTier.charAt(0).toUpperCase() + nextTier.slice(1)}
              </Button>
            ) : (
              <Button variant="outline" className="w-full" asChild>
                <Link to="/student/wallet">
                  <Wallet />
                  Manage wallet
                </Link>
              </Button>
            )}
          </section>
        </div>

        {/* Right column */}
        <div className="min-w-0">
          <Tabs value={String(tab)} onValueChange={(v) => setTab(Number(v))}>
            <TabsList className="mb-4">
              <TabsTrigger value="0">
                <User />
                Personal
              </TabsTrigger>
              <TabsTrigger value="1">
                <FileText />
                Verification
              </TabsTrigger>
              <TabsTrigger value="2">
                <Briefcase />
                Experience
              </TabsTrigger>
              <TabsTrigger value="3">
                <ShieldCheck />
                Security
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <section className="rounded-md border border-border bg-card p-4 md:p-6">
            {tab === 0 && (
              <div className="space-y-8">
                <div>
                  <SectionHeading
                    title="Basic information"
                    description="These details are used for certificate generation and employer identification."
                  />
                  <div className="grid gap-4 md:grid-cols-2">
                    <Field label="First name">
                      <Input
                        value={formData.first_name}
                        onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      />
                    </Field>
                    <Field label="Last name">
                      <Input
                        value={formData.last_name}
                        onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      />
                    </Field>
                    <Field label="Institutional student ID">
                      <Input
                        value={formData.student_id}
                        onChange={(e) => setFormData({ ...formData, student_id: e.target.value })}
                      />
                    </Field>
                    <Field label="Phone number">
                      <Input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      />
                    </Field>
                    <Field label="Gender">
                      {({ id, describedBy }) => (
                        <Select
                          value={formData.gender}
                          onValueChange={(v) => setFormData({ ...formData, gender: v })}
                        >
                          <SelectTrigger id={id} aria-describedby={describedBy}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Male">Male</SelectItem>
                            <SelectItem value="Female">Female</SelectItem>
                            <SelectItem value="Other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </Field>
                  </div>
                </div>

                <div>
                  <SectionHeading title="Professional bio" />
                  <Textarea
                    aria-label="Professional bio"
                    rows={4}
                    placeholder="Describe your career goals and key interests..."
                    value={formData.bio}
                    onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  />
                </div>
              </div>
            )}

            {tab === 1 && (
              <div className="space-y-4">
                <SectionHeading
                  title="Institutional verification"
                  description="Upload official documents to unlock high-tier job placements."
                />

                <ul className="divide-y divide-border overflow-hidden rounded-md border border-border">
                  {[
                    {
                      id: "dean_letter",
                      name: "Dean Approval Letter",
                      url: profile?.dean_letter_url,
                    },
                    {
                      id: "student_id",
                      name: "Student Institutional ID",
                      url: profile?.student_id_url,
                    },
                    { id: "resume", name: "Curriculum Vitae (CV)", url: profile?.resume_url },
                  ].map((doc) => (
                    <DocumentRow
                      key={doc.id}
                      title={doc.name}
                      isUploaded={!!doc.url}
                      isLoading={uploadingDoc === doc.id}
                      onUpload={(file: File | null | undefined) =>
                        handleDocumentUpload(doc.id, file)
                      }
                      onDownload={() => {
                        /* Logic from original file */
                      }}
                    />
                  ))}
                </ul>

                <div className="flex gap-3 rounded-md border border-dashed border-border bg-muted/40 p-4">
                  <ShieldCheck className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
                  <div>
                    <p className="text-sm font-medium text-foreground">Why verify?</p>
                    <p className="mt-0.5 text-sm text-muted-foreground">
                      Verified profiles receive 4x more engagement from partner employers and
                      qualify for recommendation letters.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {tab === 2 && (
              <div className="space-y-8">
                <div>
                  <SectionHeading
                    title="Skill set"
                    description="Select tags that define your professional capabilities."
                  />
                  <div className="flex flex-wrap gap-2">
                    {formData.skills.map((skill) => (
                      <Badge key={skill} variant="secondary" className="gap-1 pr-0.5">
                        {skill}
                        <button
                          type="button"
                          aria-label={`Remove ${skill}`}
                          onClick={() =>
                            setFormData({
                              ...formData,
                              skills: formData.skills.filter((s) => s !== skill),
                            })
                          }
                          className="rounded-xs p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <X />
                        </button>
                      </Badge>
                    ))}
                    <button
                      type="button"
                      onClick={() => {}}
                      className="inline-flex items-center gap-1 rounded-sm border border-dashed border-hairline-strong px-1.5 py-px text-xs leading-[18px] font-medium text-muted-foreground transition-colors hover:text-foreground"
                    >
                      <Plus className="size-3" aria-hidden />
                      Add skill
                    </button>
                  </div>
                </div>

                <div>
                  <SectionHeading title="Membership plans" />
                  <div className="grid gap-3 md:grid-cols-3">
                    {tiers.map((tier) => {
                      const isActive = tier.id === userTier;
                      const isUpgrade =
                        tiers.findIndex((t) => t.id === tier.id) >
                        tiers.findIndex((t) => t.id === userTier);
                      return (
                        <TierCard
                          key={tier.id}
                          tier={tier}
                          isActive={isActive}
                          isUpgrade={isUpgrade}
                          isPending={user?.verificationStatus === "pending_review"}
                          onUpgrade={() => handleUpgrade(tier.id)}
                        />
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {tab === 3 && (
              <div className="space-y-6">
                <div>
                  <SectionHeading title="Change password" />
                  <div className="grid gap-4 md:grid-cols-2">
                    <Field label="Current password">
                      <PasswordInput autoComplete="current-password" />
                    </Field>
                    <Field label="New password">
                      <PasswordInput autoComplete="new-password" />
                    </Field>
                  </div>
                  <Button variant="outline" className="mt-4">
                    Update password
                  </Button>
                </div>
                <Separator />
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm font-medium text-foreground">Two-factor authentication</p>
                    <p className="text-sm text-muted-foreground">
                      {user?.two_factor_enabled
                        ? "Your account is protected with email-based 2FA."
                        : "Secure your account with an extra layer of protection."}
                    </p>
                  </div>
                  {user?.two_factor_enabled ? (
                    <Button variant="destructive-outline" onClick={handleDisable2FA}>
                      Disable
                    </Button>
                  ) : (
                    <Button variant="outline" onClick={handleEnable2FA}>
                      Enable
                    </Button>
                  )}
                </div>
              </div>
            )}
          </section>
        </div>
      </div>

      {/* 2FA setup dialog */}
      <Dialog
        open={twoFactorDialogOpen}
        onOpenChange={(o) => !o && setTwoFactorDialogOpen(false)}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Confirm 2FA activation</DialogTitle>
            <DialogDescription>
              We've sent a 6-digit verification code to your email. Enter it below to enable 2FA.
            </DialogDescription>
          </DialogHeader>
          <Field label="Verification code">
            <Input
              inputMode="numeric"
              autoComplete="one-time-code"
              className="font-mono tracking-widest"
              value={twoFactorCode}
              onChange={(e) => setTwoFactorCode(e.target.value)}
              placeholder="000000"
            />
          </Field>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTwoFactorDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={verify2FASetup} disabled={twoFactorCode.length < 6}>
              Confirm & enable
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AvatarUploadModal
        open={avatarModalOpen}
        onClose={() => setAvatarModalOpen(false)}
        onUploadSuccess={() => window.location.reload()}
      />

      <PaymentModal
        open={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        onSubmit={handlePaymentSuccess}
        amount={getAmountForTier(selectedTier)}
        description={
          selectedTier.charAt(0).toUpperCase() + selectedTier.slice(1) + " Membership Upgrade"
        }
        courseName={
          tiers.find((t: any) => t.id === selectedTier)?.name || "Membership Upgrade"
        }
      />
    </div>
  );
}

// --- Subcomponents ---

function SectionHeading({ title, description }: { title: string; description?: string }) {
  return (
    <div className="mb-4">
      <h3 className="text-[15px] leading-6 font-medium text-foreground">{title}</h3>
      {description && <p className="text-sm text-muted-foreground">{description}</p>}
    </div>
  );
}

function InfoItem({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value?: string;
}) {
  return (
    <li className="flex items-center gap-3">
      <span
        className="flex size-8 shrink-0 items-center justify-center rounded-sm border border-border bg-background text-muted-foreground [&_svg]:size-4"
        aria-hidden
      >
        {icon}
      </span>
      <div className="min-w-0">
        <p className="eyebrow">{label}</p>
        <p className="truncate text-sm text-foreground">{value}</p>
      </div>
    </li>
  );
}

function DocumentRow({ title, isUploaded, isLoading, onUpload, onDownload }: any) {
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 bg-card px-4 py-3 transition-colors hover:bg-accent/50">
      <div className="flex min-w-0 items-center gap-3">
        <span
          className={cn(
            "flex size-9 shrink-0 items-center justify-center rounded-sm border [&_svg]:size-4",
            isUploaded
              ? "border-success/30 bg-success/10 text-success"
              : "border-border bg-background text-muted-foreground",
          )}
          aria-hidden
        >
          {isUploaded ? <CheckCircle2 /> : <Clock />}
        </span>
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-foreground">{title}</p>
          <p className="text-caption text-muted-foreground">
            {isUploaded ? "Verified Document" : "Awaiting Upload"}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-1">
        {isUploaded && <IconAction label={`Download ${title}`} icon={<Download />} onClick={onDownload} />}
        <Button asChild size="sm" variant={isUploaded ? "ghost" : "outline"}>
          <label className="cursor-pointer">
            {isLoading ? "..." : isUploaded ? "Replace" : "Upload"}
            <input
              hidden
              type="file"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => onUpload(e.target.files?.[0])}
            />
          </label>
        </Button>
      </div>
    </li>
  );
}

function TierCard({ tier, isActive, isUpgrade, isPending, onUpgrade }: any) {
  return (
    <div
      className={cn(
        "flex flex-col rounded-md border p-4",
        isActive ? "border-foreground/60 bg-muted/40" : "border-border",
      )}
    >
      <p className="eyebrow mb-2">{tier.shortLabel}</p>
      <p className="text-sm font-medium text-foreground">{tier.name}</p>
      <p className="mb-3 text-sm text-muted-foreground">{tier.priceLabel}</p>

      <ul className="mb-4 flex-1 space-y-1.5">
        {tier.features.slice(0, 2).map((f: string, i: number) => (
          <li key={i} className="flex items-start gap-2 text-caption text-body">
            <Check className="mt-0.5 size-3.5 shrink-0 text-success" aria-hidden />
            {f}
          </li>
        ))}
      </ul>

      {isActive ? (
        <Badge variant="success" dot className="self-start">
          Active
        </Badge>
      ) : isPending && tier.id !== "free" ? (
        <Button size="sm" variant="outline" disabled className="w-full">
          Pending review
        </Button>
      ) : isUpgrade ? (
        <Button size="sm" variant="outline" onClick={onUpgrade} className="w-full">
          Upgrade
        </Button>
      ) : null}
    </div>
  );
}

export default StudentProfile;
